import type { FunctionComponent } from 'react';
import React, { useCallback } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { CollabServiceProperty } from '@confluence/native-collab';
import { useSessionData } from '@confluence/session-data';
import { useSpaceId } from '@confluence/space-utils';

import {
	extendPayloadAttributes,
	extractAttributesFromContext,
	getProseMirrorFMPTimestamp,
} from './helpers';

interface EditorAnalyticsListenerProps {
	contentId?: string;
	contentType?: string;
	source?: string;
	collabService?: CollabServiceProperty;
	children?: React.ReactNode;
	onProseMirrorRendered?: Function;
	isGuest?: boolean;
	touchpointId?: string;
}

const handleOnProseMirrorRendered = (onProseMirrorRendered, eventAttributes) => {
	if (onProseMirrorRendered && typeof onProseMirrorRendered === 'function') {
		const clientRouteStart = performance.getEntriesByName('browser-metric.start').pop()?.startTime;
		const proseMirrorRenderedTimestamp = getProseMirrorFMPTimestamp(
			eventAttributes?.startTime,
			eventAttributes?.duration,
		);
		const proseMirrorRenderedFMP =
			typeof clientRouteStart !== 'undefined' && typeof proseMirrorRenderedTimestamp !== 'undefined'
				? Math.round(proseMirrorRenderedTimestamp - clientRouteStart)
				: proseMirrorRenderedTimestamp;
		onProseMirrorRendered({
			...eventAttributes,
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
		});
	}
};

export const EditorAnalyticsListener: FunctionComponent<EditorAnalyticsListenerProps> = (props) => {
	const isLivePage = useIsCurrentPageLive();
	const spaceId = useSpaceId();
	const { edition, environment } = useSessionData() || {};
	// setting default to development so in tests it doesn't fail due to being undefined
	const safeEnvironment = environment || 'DEVELOPMENT';
	const {
		contentId,
		contentType,
		children,
		source,
		collabService,
		onProseMirrorRendered,
		isGuest,
		touchpointId,
	} = props;

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const instanceName: string | undefined = safeEnvironment;
			if (event?.payload?.action === 'proseMirrorRendered') {
				handleOnProseMirrorRendered(onProseMirrorRendered, event?.payload?.attributes);
			}

			event.update((payload) => {
				const isCommentEvent = payload.attributes && payload.attributes.appearance === 'comment';

				// WS-3190 If this is a comment event use the existing objectId as it correlates to the commentId
				let newPayload = extendPayloadAttributes(payload, {
					objectId: isCommentEvent ? payload.attributes.objectId : contentId,
					editorSource: source,
				}) as AnalyticsEventPayload;

				if (instanceName) {
					newPayload = extendPayloadAttributes(newPayload, {
						instance: instanceName,
					});
				}

				newPayload = extendPayloadAttributes(newPayload, {
					collabService: collabService ?? 'none',
					isLivePage,
					edition,
					spaceId,
				});

				if (isGuest !== undefined) {
					newPayload = extendPayloadAttributes(newPayload, {
						isGuest,
					});
				}

				if (touchpointId) {
					newPayload = extendPayloadAttributes(newPayload, {
						touchpointId,
					});
				}

				newPayload.source = source || 'unknown';

				if (event.context) {
					const attributesFromContext = extractAttributesFromContext(event.context);
					newPayload.attributes = {
						...newPayload.attributes,
						...attributesFromContext,
					};
				}

				return newPayload;
			});
		},
		[
			contentId,
			source,
			collabService,
			onProseMirrorRendered,
			edition,
			safeEnvironment,
			isLivePage,
			spaceId,
			isGuest,
			touchpointId,
		],
	);

	const handleFabricElementsEvents = useCallback(
		(event: UIAnalyticsEvent) => {
			event.update((payload) => {
				return extendPayloadAttributes(payload, {
					pageType: contentType,
					editorSource: source,
					objectId: contentId,
					objectType: contentType,
					spaceId,
					isLivePage,
				});
			});
		},
		[contentId, contentType, source, isLivePage, spaceId],
	);

	return (
		<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.editor}>
			<AnalyticsListener onEvent={handleFabricElementsEvents} channel={FabricChannel.elements}>
				<AnalyticsListener onEvent={handleFabricElementsEvents} channel={FabricChannel.media}>
					{children}
				</AnalyticsListener>
			</AnalyticsListener>
		</AnalyticsListener>
	);
};

EditorAnalyticsListener.defaultProps = {};
